/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import Link from 'flarum/common/components/Link';
import IndexPage, { type IIndexPageAttrs } from 'flarum/forum/components/IndexPage';
import ItemList from 'flarum/common/utils/ItemList';
import Page from 'flarum/common/components/Page';
import ReviewList from './ReviewList';
import ReviewPage from './ReviewPage';
import IndexPageHero from 'flarum/newvigation/components/IndexPageHero';
import { utils } from '@glowingblue-orell-thalia-common';

const { findChild } = utils.vdom;
import type { VdomParent } from '@glowingblue-orell-thalia-common/utils/vdom';
import extractText from 'flarum/common/utils/extractText';
import type Mithril from 'mithril';
import type Tag from 'flarum/tags/common/models/Tag';

/**
 * IndexPage, listing reviews instead of discussions. Routed in ../extend/setupRouting.
 *
 * CORE_CODE_OVERRIDE
 */
export default class ReviewsPage extends IndexPage {
	/**
	 * Follows core's IndexPage closely — re-check it on every core upgrade.
	 *
	 * CORE_CODE_OVERRIDE
	 */
	oninit(vnode: Mithril.Vnode<IIndexPageAttrs, this>) {
		// Grandparent oninit(), skipping IndexPage's.
		Reflect.apply(Page.prototype.oninit, this, [vnode]);

		// Remember where we came from, so the view can scroll that review back into position.
		if (app.previous.matches(ReviewPage)) {
			this.lastDiscussion = app.previous.get('review');
		}

		// Coming from the list means a changed parameter or a deliberate refresh.
		if (app.previous.matches(IndexPage)) {
			app.reviews.clear();
		}

		app.reviews.refreshParams(app.search.params(), Number.parseInt(m.route.param('page')) || 1);

		if (this.currentTag && typeof this.currentTag === 'function') {
			// `title` is typed as required, but passing one would change the history entry;
			// setTitle() sets it instead.
			(app.history.push as (name: string) => void)('reviews_tag');
		} else {
			app.history.push('reviews', extractText(app.translator.trans('glowingblue-orell-rezensionen.forum.header.back_to_reviews_tooltip')));
		}

		this.bodyClass = 'App--reviews';
		this.scrollTopOnCreate = false;
	}

	setTitle() {
		if (this.currentTag && typeof this.currentTag === 'function') {
			const tag = this.currentTag();
			if (tag) {
				app.setTitle(tag.name());
				app.setTitleCount(0);
				return;
			}
		}

		app.setTitle(extractText(app.translator.trans('glowingblue-orell-rezensionen.forum.reviews.nav')));
		app.setTitleCount(0);
	}

	view() {
		const vnode = super.view();

		const listContainer = findChild(vnode as VdomParent, 'IndexPage-results', true) as Mithril.Vnode;
		(listContainer.children as Mithril.ChildArray)[1] = <ReviewList state={app.reviews} />;

		return vnode;
	}

	hero() {
		const title = app.translator.trans('glowingblue-orell-rezensionen.forum.reviews.nav');
		const icon = 'far fa-heart';

		let tag: Tag | undefined;
		if (this.currentTag && typeof this.currentTag === 'function') {
			tag = this.currentTag();
		}

		let navItems = this.navItems();

		return <IndexPageHero currentTag={tag} navItems={navItems} title={title} icon={icon} href={app.route.reviews()} type="reviews" />;
	}

	/**
	 * CORE_CODE_OVERRIDE
	 */
	sidebarItems() {
		const items = super.sidebarItems();

		items.remove('newDiscussion');
		items.remove('subscription');

		const title = app.translator.trans('glowingblue-orell-rezensionen.forum.index.all_tags');
		items.add('title', <h3>{this.currentTag() ? <Link href={app.route.reviews()}>{title}</Link> : title}</h3>, 150);

		return items;
	}

	/**
	 * Copied from core, sorting reviews instead of discussions.
	 *
	 * CORE_CODE_OVERRIDE
	 */
	viewItems() {
		const items = new ItemList<Mithril.Children>();

		const sortMap = app.reviews.sortMap();

		const sortOptions: Record<string, Mithril.Children> = {};
		for (const i in sortMap) {
			sortOptions[i] = app.translator.trans('core.forum.index_sort.' + i + '_button');
		}

		items.add(
			'sort',
			Dropdown.component(
				{
					buttonClassName: 'Button',
					label: sortOptions[app.search.params().sort] || Object.keys(sortMap).map((key) => sortOptions[key])[0],
					accessibleToggleLabel: app.translator.trans('core.forum.index_sort.toggle_dropdown_accessible_label'),
				},
				Object.keys(sortOptions).map((value) => {
					const label = sortOptions[value];
					const active = (app.search.params().sort || Object.keys(sortMap)[0]) === value;

					return Button.component(
						{
							icon: active ? 'fas fa-check' : true,
							onclick: app.search.changeSort.bind(app.search, value),
							active: active,
						},
						label
					);
				})
			)
		);

		return items;
	}

	/**
	 * Copied from core, refreshing reviews instead of discussions.
	 *
	 * CORE_CODE_OVERRIDE
	 */
	actionItems() {
		const originalItems = super.actionItems();

		const items = new ItemList<Mithril.Children>();

		items.add(
			'refresh',
			Button.component({
				title: app.translator.trans('core.forum.index.refresh_tooltip'),
				icon: 'fas fa-sync',
				className: 'Button Button--icon',
				onclick: () => {
					app.reviews.refresh();

					if (app.session.user) {
						app.store.find('users', app.session.user.id()!);
						m.redraw();
					}
				},
			})
		);

		if (app.session.user) {
			items.add('markAllAsRead', originalItems.get('markAllAsRead'));
		}

		return items;
	}
}
